import { z } from "zod";
import { defineAd } from "../core/ad-definition";
import { adlandAPI } from "../services/adland.api";

/** `x.com/<user>/status/<id>`, on either domain, however it was decorated. */
const TWEET_URL = /(?:twitter\.com|x\.com)\/\w+\/status\/\d+/;

/**
 * X Post Ad Definition
 *
 * The metadata is deliberately identical in shape to a cast's — display name,
 * handle, avatar, text, time — because the two render as the same card. Which
 * network a post was written on is a badge, not a layout.
 *
 * What is stored is a permalink, not an ID: opening the ad means opening the
 * post, and every host this runs in knows how to open a URL. The URL kept is
 * the one the API rebuilt from the post itself rather than the one pasted, so
 * an ad does not carry someone's tracking parameters around forever.
 */
export const tweetAd = defineAd({
  type: "tweet",
  data: z.object({
    url: z.string().nonempty("URL is required"),
  }),
  metadata: z.object({
    displayName: z.string().optional(),
    username: z.string().optional(),
    pfpUrl: z.string().optional(),
    text: z.string(),
    /** Epoch milliseconds, as a string — the same convention a cast uses. */
    timestamp: z.string(),
    /**
     * Every attachment, in the order the post shows them.
     *
     * Optional rather than defaulted to `[]`, because an ad pinned before this
     * field existed has no `media` key and an empty array would claim the post
     * had no pictures rather than that this ad predates the question.
     */
    media: z
      .array(
        z.object({
          type: z.string(),
          url: z.string(),
          width: z.number().nullable().optional(),
          height: z.number().nullable().optional(),
          alt: z.string().nullable().optional(),
        }),
      )
      .optional(),
    /** A still of the first attachment. Kept alongside `media` so cards that
     * read it by name keep working on ads pinned before `media` existed. */
    image: z.string().optional(),
  }),
  async verify({ url }) {
    if (!TWEET_URL.test(url)) {
      throw new Error(
        "Not an X post link — expected https://x.com/user/status/123",
      );
    }

    const res = await adlandAPI.verify.tweet({ url });
    if (!res.verified) {
      throw new Error(
        "That post could not be read — it may be deleted, or from a protected account",
      );
    }
  },
  async getMetadata({ url }) {
    const tweet = await adlandAPI.metadata.tweet({ url });
    return {
      displayName: tweet.author.name,
      username: tweet.author.username,
      pfpUrl: tweet.author.pfpUrl,
      text: tweet.text,
      timestamp: String(Date.parse(tweet.createdAt)),
      media: tweet.media?.length ? tweet.media : undefined,
      image: tweet.image ?? undefined,
    };
  },
});

/**
 * Type inference for TweetAd data
 */
export type TweetAdData = z.infer<typeof tweetAd.data>;

/**
 * Type inference for TweetAd metadata
 */
export type TweetAdMetadata = z.infer<NonNullable<typeof tweetAd.metadata>>;

export type TweetAd = {
  type: "tweet";
  data: TweetAdData;
  metadata: TweetAdMetadata;
};
